import HttpFactory from "~/repository/factory";
import { createPublicRequestConfig } from "~/repository/authSession";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  CancelSubscriptionFeedbackRequest,
  CancelSubscriptionFeedbackResponse,
  CardTokenInput,
  CardTokenResponse,
  CreateSubscriptionInput,
  CreateSubscriptionResponse,
  OrderStatusResponse,
  UpdateCreditCardInput,
  UserPaymentDataInput,
  UserPaymentDataResponse,
  UserSubscriptionChargeResponse,
  UserSubscriptionResponse,
  ValidateCouponCodeInput,
  ValidateCouponCodeResponse,
} from "~/types/payment";

class PaymentModule extends HttpFactory {
  async getUserPaymentData(): Promise<UserPaymentDataResponse> {
    return await this.get(`${ENDPOINTS.PAYMENT.USER_PAYMENT_DATA}`);
  }

  async updateUserPaymentData(
    userPaymentData: UserPaymentDataInput,
  ): Promise<UserPaymentDataResponse> {
    return await this.put(
      `${ENDPOINTS.PAYMENT.USER_PAYMENT_DATA}`,
      userPaymentData,
    );
  }

  async generateCardToken(
    cardData: CardTokenInput,
  ): Promise<CardTokenResponse> {
    const { $config } = useNuxtApp();
    const appId = $config.public.PAGARME_APP_ID;

    return await this.post(
      `${ENDPOINTS.PAGAR_ME.GENERATE_CARD_TOKEN.replace("{appId}", appId)}`,
      cardData,
      createPublicRequestConfig({
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }),
    );
  }

  async createSubscription(
    data: CreateSubscriptionInput,
  ): Promise<CreateSubscriptionResponse> {
    return await this.post(`${ENDPOINTS.PAYMENT.SUBSCRIPTIONS}`, data);
  }

  async getOrderStatus(orderId: number | string): Promise<OrderStatusResponse> {
    return await this.get(`${ENDPOINTS.PAYMENT.ORDERS}${orderId}/status`);
  }

  async getUserSubscription(): Promise<UserSubscriptionResponse> {
    return await this.get(`${ENDPOINTS.PAYMENT.USER_SUBSCRIPTION}`);
  }

  async claimSubscriptionFreeDay(): Promise<UserSubscriptionResponse> {
    return await this.post(
      `${ENDPOINTS.PAYMENT.CLAIM_SUBSCRIPTION_FREE_DAY}`,
      {},
    );
  }

  async getUserCharges(
    subscriptionId: string,
  ): Promise<UserSubscriptionChargeResponse[]> {
    return await this.get(
      `${ENDPOINTS.PAYMENT.SUBSCRIPTIONS}${subscriptionId}/charges`,
    );
  }

  async validateCouponCode(
    data: ValidateCouponCodeInput,
  ): Promise<ValidateCouponCodeResponse> {
    return await this.post(`${ENDPOINTS.PAYMENT.COUPON_CODES}`, data);
  }

  async saveCancelSubscriptionFeedback(
    data: CancelSubscriptionFeedbackRequest,
  ): Promise<CancelSubscriptionFeedbackResponse> {
    return await this.post(
      `${ENDPOINTS.PAYMENT.CANCEL_SUBSCRIPTION_FEEDBACK}`,
      data,
    );
  }

  async updateCreditCard(data: UpdateCreditCardInput): Promise<void> {
    return await this.post(
      `${ENDPOINTS.PAYMENT.UPDATE_SUBSCRIPTION_CREDIT_CARD}`,
      data,
    );
  }
}

export default PaymentModule;
